import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, ArrowRight, ArrowUpRight, Home as HomeIcon, Briefcase,
  UserCheck, Sparkles, CheckCircle2, Users, Star, Quote,
  MessageCircle, TrendingUp, ShieldCheck, Heart, Handshake, Rocket, Network,
} from "lucide-react";
import api from "../lib/api";
import ProfileCard from "../components/ProfileCard";
import CategoryTile from "../components/CategoryTile";
import PartnersStrip from "../components/PartnersStrip";
import HScroll from "../components/HScroll";
import useSEO from "../hooks/useSEO";
import usePageContent from "../hooks/usePageContent";
import PageContent from "../components/PageContent";

export default function Home() {
  useSEO({
    title: "Accueil",
    description: "DemkaBiz — Les services d'établissement et d'entrepreneuriat en un seul clic. Trouvez des professionnels, entrepreneurs et organismes francophones partout au Canada.",
  });
  const [q, setQ] = useState("");
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const heroSubtitle = usePageContent("home.hero.subtitle", "La place de marché francophone qui connecte les nouveaux arrivants, les entrepreneurs et les organismes à travers le Canada.");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/profiles", { params: { featured: true, limit: 6 } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const catsV1 = categories.filter((c) => c.volet === "etablissement");
  const catsV2 = categories.filter((c) => c.volet === "entrepreneuriat");

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/recherche${q ? `?search=${encodeURIComponent(q)}` : ""}`);
  };

  const entries = [
    {
      title: "Je m'établis",
      desc: "Soutien à l'établissement, démarches, logement, services essentiels.",
      to: "/je-metablis",
      icon: HomeIcon,
      image: "/media/visuels/porte-etablir.jpg",
      featured: true,
    },
    {
      title: "J'entreprends",
      desc: "Lancez ou développez votre entreprise au Canada.",
      to: "/j-entreprends",
      icon: Briefcase,
      image: "/media/visuels/porte-entreprendre.jpg",
    },
    {
      title: "Je suis professionnel",
      desc: "Référencez votre activité et trouvez vos prochains clients.",
      to: "/inscription?role=professional",
      icon: UserCheck,
      image: "/media/visuels/porte-professionnel.jpg",
    },
    {
      title: "Je cherche un service",
      desc: "Trouvez le bon prestataire francophone près de chez vous.",
      to: "/je-cherche-un-service",
      icon: Sparkles,
      image: "/media/visuels/porte-service.jpg",
    },
  ];

  const pillars = [
    { icon: Users, title: "TROUVEZ", desc: "des professionnels, des entrepreneurs et des organismes de confiance.", color: "primary" },
    { icon: MessageCircle, title: "ÉCHANGEZ", desc: "directement via messagerie intégrée ou WhatsApp.", color: "accent" },
    { icon: TrendingUp, title: "DÉVELOPPEZ", desc: "votre réseau, votre visibilité et vos opportunités d'affaires.", color: "primary" },
    { icon: ShieldCheck, title: "AVANCEZ", desc: "en toute confiance avec des avis réciproques et des services de qualité.", color: "accent" },
  ];

  const accueilSteps = [
    { icon: Heart, title: "ACCUEIL", desc: "Nous vous accueillons et vous accompagnons." },
    { icon: Handshake, title: "INTÉGRATION", desc: "Nous facilitons votre établissement et votre inclusion au Canada." },
    { icon: Rocket, title: "ENTREPRENEURIAT", desc: "Nous propulsons vos idées et soutenons votre succès entrepreneurial." },
    { icon: Network, title: "RÉSEAUTAGE", desc: "Nous connectons les bonnes personnes au bon moment." },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO — editorial sand glow + sculpted keywords + glassmorphism search */}
      <section className="relative overflow-hidden editorial-sand-glow" data-testid="hero-section">
        <div className="hero-blob bg-primary/20 w-[520px] h-[520px] -top-40 -right-24" />
        <div className="hero-blob bg-accent/25 w-[440px] h-[440px] top-56 -left-24" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">
          <div className="lg:col-span-7 reveal-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur-md border border-primary/15 text-xs text-primary font-semibold uppercase tracking-widest mb-8 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              Services aux immigrants et entrepreneurs · Canada
            </div>
            <h1 className="font-display font-black text-4xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] text-foreground tracking-tight leading-[0.98]">
              <span className="block text-primary">S'établir.</span>
              <span className="block text-foreground">Entreprendre.</span>
              <span className="block text-accent italic font-light">Réussir.</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed font-normal" data-testid="home-hero-subtitle">
              <PageContent value={heroSubtitle} />
            </p>

            <form onSubmit={handleSearch} className="mt-10 relative max-w-2xl" data-testid="hero-search-form">
              <div className="flex flex-col sm:flex-row gap-2 p-2 bg-white/85 backdrop-blur-xl border border-primary/15 rounded-full shadow-[0_20px_50px_-20px_rgba(14,139,133,0.35)]">
                <div className="relative flex-1">
                  <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                  <input
                    data-testid="hero-search-input"
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Trouvez les meilleurs services près de chez vous…"
                    className="w-full pl-12 pr-4 py-3.5 bg-transparent outline-none text-base sm:text-sm placeholder:text-muted-foreground/70"
                  />
                </div>
                <button type="submit" className="btn-orange px-6 py-3.5 w-full sm:w-auto shadow-[0_8px_25px_rgba(249,115,22,0.35)] active:scale-[0.98]" data-testid="hero-search-submit">
                  Rechercher <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-foreground/70 font-medium">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Profils vérifiés</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> 100% francophone</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Partout au Canada</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="reveal-up relative" style={{ animationDelay: "0.2s" }}>
              <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-20px_rgba(14,139,133,0.35)] border border-white/40 bg-secondary">
                <img
                  src="/media/visuels/couple-demkabiz.jpg"
                  alt="Deux membres de la communauté DemkaBiz échangeant devant un ordinateur portable, une tasse DemkaBiz à la main"
                  className="w-full h-auto object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/20 rounded-[2rem] pointer-events-none" />
              </div>
              {/* Floating rating badge - glassmorphism */}
              <div className="absolute -bottom-5 -left-5 hidden md:flex items-center gap-3 px-5 py-3.5 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/60 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)]" data-testid="hero-badge">
                <div className="w-11 h-11 rounded-full bg-accent/15 flex items-center justify-center text-accent">
                  <Star className="w-5 h-5 fill-current" />
                </div>
                <div>
                  <div className="font-display font-bold text-lg leading-tight">4.8/5</div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Note moyenne des membres</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KINETIC EDITORIAL RIBBON — signature moment */}
      <div className="kinetic-ribbon" data-testid="kinetic-ribbon" aria-hidden="true">
        <div className="kinetic-track">
          {[...Array(2)].map((_, k) => (
            <span key={k}>
              <span className="uppercase">S'établir</span><i className="kinetic-dot" />
              <span className="uppercase">Entreprendre</span><i className="kinetic-dot" />
              <span className="uppercase">Réussir</span><i className="kinetic-dot" />
              <span className="italic opacity-90">Hub d'affaires Canada · Afrique</span><i className="kinetic-dot" />
              <span className="uppercase">Francophone</span><i className="kinetic-dot" />
              <span className="uppercase">Partout au Canada</span><i className="kinetic-dot" />
            </span>
          ))}
        </div>
      </div>

      {/* 4 PILIERS — Tetris style with filigrane numerals */}
      <section className="editorial-sand-glow py-20 lg:py-28 border-b border-primary/10 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-primary font-bold mb-3">Comment nous vous aidons</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-foreground tracking-tight leading-[1.05]">
              Tout ce qu'il vous faut,<br /><span className="text-primary italic font-light">en un seul endroit.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {pillars.map((p, i) => (
              <div
                key={p.title}
                className={`group relative p-7 lg:p-8 rounded-3xl bg-white border border-primary/10 shadow-[0_4px_20px_rgba(0,0,0,0.03)] card-lift overflow-hidden ${i % 2 === 1 ? "lg:translate-y-6" : ""}`}
                data-testid={`pilier-${i}`}
              >
                <div className="step-numeral absolute -right-2 -top-6">{String(i + 1).padStart(2, "0")}</div>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center relative z-10 ${p.color === "primary" ? "bg-primary text-white" : "bg-accent text-white"} shadow-lg`}>
                  <p.icon className="w-6 h-6" />
                </div>
                <h3 className="font-display font-bold text-base mt-5 tracking-[0.15em] relative z-10">{p.title}</h3>
                <p className="text-sm text-muted-foreground mt-2.5 leading-relaxed relative z-10">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POURQUOI NOUS — Bento Grid asymmetric with dark hero card */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="why-us-section">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.25em] text-accent font-bold mb-3">Pourquoi nous choisir</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-foreground tracking-tight leading-[1.05]">
            La plateforme qui vous <span className="text-primary italic font-light">ressemble</span>.
          </h2>
        </div>
        <div className="grid grid-cols-12 gap-4 lg:gap-5" data-testid="why-us-bento-grid">
          {/* Dominant hero bento — Pont Canada-Afrique */}
          <div className="col-span-12 lg:col-span-8 lg:row-span-2 relative overflow-hidden rounded-3xl bento-hero-card p-8 sm:p-10 lg:p-12 min-h-[280px] lg:min-h-[380px] shadow-2xl" data-testid="why-us-2">
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-accent/25 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/12 backdrop-blur-md border border-white/20 text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-100 mb-6">
                <Network className="w-3 h-3" /> Pilier signature
              </div>
              <h3 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.05] max-w-xl">Pont Canada-Afrique</h3>
              <p className="mt-5 text-base sm:text-lg text-white/85 max-w-xl leading-relaxed">
                Nous connectons les deux rives pour les nouveaux arrivants et les entrepreneurs de la diaspora.
              </p>
            </div>
          </div>
          {/* 100% francophone */}
          <div className="col-span-12 sm:col-span-6 lg:col-span-4 relative overflow-hidden rounded-3xl bg-white border border-primary/10 p-7 shadow-sm card-lift" data-testid="why-us-0">
            <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
              <span className="font-display font-black text-lg">01</span>
            </div>
            <h3 className="font-display font-bold text-lg mt-4 leading-snug">100% francophone</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Une expérience pensée dans votre langue, avec des prestataires qui la parlent aussi.</p>
          </div>
          {/* Humaine et incarnée */}
          <div className="col-span-12 sm:col-span-6 lg:col-span-4 relative overflow-hidden rounded-3xl bg-white border border-accent/15 p-7 shadow-sm card-lift" data-testid="why-us-1">
            <div className="w-11 h-11 rounded-2xl bg-accent/10 text-accent flex items-center justify-center">
              <span className="font-display font-black text-lg">02</span>
            </div>
            <h3 className="font-display font-bold text-lg mt-4 leading-snug">Humaine et incarnée</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Une vraie équipe derrière chaque demande, prête à écouter et orienter.</p>
          </div>
          {/* Sans frais cachés */}
          <div className="col-span-12 sm:col-span-6 lg:col-span-6 relative overflow-hidden rounded-3xl bg-white border border-primary/10 p-7 shadow-sm card-lift" data-testid="why-us-3">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <span className="font-display font-black text-lg">04</span>
              </div>
              <div>
                <h3 className="font-display font-bold text-lg leading-snug">Sans frais cachés</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">L'inscription est gratuite. Vous négociez directement avec le prestataire.</p>
              </div>
            </div>
          </div>
          {/* Sécurité & confiance */}
          <div className="col-span-12 sm:col-span-6 lg:col-span-6 relative overflow-hidden rounded-3xl bg-white border border-accent/15 p-7 shadow-sm card-lift" data-testid="why-us-4">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
                <span className="font-display font-black text-lg">05</span>
              </div>
              <div>
                <h3 className="font-display font-bold text-lg leading-snug">Sécurité & confiance</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Profils vérifiés, avis modérés, conformité Loi 25 du Québec.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 ENTRY POINTS — bigger cards with bottom-only scrim + hover chevron */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="entry-doors-section">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-accent font-bold mb-3">Vos portes d'entrée</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-foreground max-w-2xl tracking-tight leading-[1.05]">
              Par où souhaitez-vous <span className="text-primary italic font-light">commencer</span> ?
            </h2>
          </div>
          <p className="text-base text-muted-foreground max-w-md leading-relaxed">
            Quatre parcours pour vous accompagner, que vous soyez nouvel arrivant, prestataire ou simple client.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
          {entries.map((e, idx) => (
            <Link
              key={e.title}
              to={e.to}
              data-testid={`entry-${e.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              aria-label={`${e.title} — ${e.desc}`}
              className="relative group aspect-[2/1] rounded-[2rem] overflow-hidden border border-primary/10 shadow-[0_20px_40px_-20px_rgba(14,139,133,0.2)] hover:shadow-[0_30px_60px_-20px_rgba(14,139,133,0.35)] transition-all duration-500 bg-secondary"
            >
              <img
                src={e.image}
                alt={e.title}
                className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                loading={idx < 2 ? "eager" : "lazy"}
              />
              {/* Bottom-only scrim */}
              <div className="absolute inset-0 doorway-scrim opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              {/* Hover explore pill */}
              <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-400">
                <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/95 backdrop-blur text-primary text-xs font-semibold shadow-lg">
                  Explorer <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* STATS BAND — Swiss Precision ribbon on dark teal */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-testid="stats-band">
        <div className="rounded-[2rem] relative overflow-hidden teal-depth-scrim text-white p-8 sm:p-12">
          <div className="absolute inset-0 opacity-20 pointer-events-none demka-grain" />
          <div className="relative grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { n: "+500", l: "Prestataires actifs" },
              { n: "4,8", suffix: "/5", l: "Note moyenne" },
              { n: "13", l: "Provinces & territoires" },
              { n: "100%", l: "Francophone" },
            ].map((s, i) => (
              <div key={i} className="text-center relative">
                {i > 0 && <div className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 w-px h-16 bg-white/15" aria-hidden />}
                <div className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-amber-300">
                  {s.n}{s.suffix && <span className="text-2xl text-white/70 font-normal">{s.suffix}</span>}
                </div>
                <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-white/70 mt-3 font-semibold">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 ACCOMPAGNEMENT STEPS — refined dark section */}
      <section className="relative overflow-hidden py-20 lg:py-28 mt-8">
        <div className="absolute inset-0 teal-depth-scrim" aria-hidden />
        <div className="hero-blob bg-accent/40 w-[500px] h-[500px] -top-32 -right-32" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-amber-300 font-bold mb-3">Notre accompagnement</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Une initiative pensée <span className="italic font-light text-amber-200">pour vous</span>.
            </h2>
            <p className="text-white/80 mt-5 text-base sm:text-lg leading-relaxed">Une initiative de Demka Services Inc. au service de votre intégration et de votre réussite par l'entrepreneuriat.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {accueilSteps.map((s, i) => (
              <div key={s.title} className="relative rounded-3xl bg-white/8 backdrop-blur-md p-7 border border-white/15 hover:bg-white/12 hover:border-white/30 hover:-translate-y-1 transition-all duration-300" data-testid={`accompany-${i}`}>
                <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center shadow-lg"><s.icon className="w-5 h-5" /></div>
                <h3 className="font-display font-bold text-lg mt-5 tracking-[0.12em]">{s.title}</h3>
                <p className="text-sm text-white/80 mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ARCHITECTURE DES SERVICES — pill tabs + refined category tiles */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-primary font-bold mb-3">Architecture des services</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-foreground tracking-tight leading-[1.05]">
            Deux volets <span className="text-primary italic font-light">complémentaires</span>.
          </h2>
          <p className="text-muted-foreground mt-5 text-base sm:text-lg leading-relaxed">DemkaBiz organise son offre pour accompagner les personnes immigrantes à chaque étape de leur parcours au Canada.</p>
        </div>

        <ArchitectureVolets catsV1={catsV1} catsV2={catsV2} />
      </section>

      {/* FEATURED PROFILES */}
      {featured.length > 0 && (
        <section className="editorial-sand-glow py-20" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between gap-4 mb-10">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-accent font-bold mb-3">En vedette</p>
                <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-foreground tracking-tight leading-[1.05]">
                  Prestataires <span className="italic font-light text-primary">recommandés</span>
                </h2>
              </div>
              <Link to="/annuaire/professionnels" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all">
                Voir l'annuaire <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <HScroll testId="featured-carousel" itemWidthClass="w-72 sm:w-80">
              {featured.map((p) => (
                <ProfileCard key={p.id} profile={p} />
              ))}
            </HScroll>
          </div>
        </section>
      )}

      {/* ICI QUÉBEC */}
      <IciQuebecStrip />

      {/* LATEST BLOG POSTS */}
      <LatestBlogPosts />

      {/* EVENTS SPOTLIGHT */}
      <EventsSpotlight />

      {/* AD BANNER SLOT */}
      <AdBannerSection />

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="editorial-sand-glow py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-xs uppercase tracking-[0.25em] text-primary font-bold mb-3">Témoignages</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-foreground max-w-3xl mb-12 tracking-tight leading-[1.05]">
              Ils nous font <span className="italic font-light text-primary">confiance</span>
            </h2>
            <HScroll testId="testimonials-carousel" itemWidthClass="w-80 sm:w-96 lg:w-[420px]">
              {testimonials.map((t) => (
                <div key={t.id} className="card-surface p-8 h-full card-lift" data-testid={`testimonial-${t.id}`}>
                  <Quote className="w-8 h-8 text-accent/30 mb-4" />
                  <p className="text-foreground/90 leading-relaxed text-[0.95rem]">{t.content}</p>
                  <div className="flex items-center gap-3 mt-6 pt-5 border-t border-primary/10">
                    {t.photo_url && <img src={t.photo_url} alt={t.author} className="w-12 h-12 rounded-full object-cover ring-2 ring-primary/20" />}
                    <div>
                      <div className="font-display font-bold">{t.author}</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wider">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </HScroll>
          </div>
        </section>
      )}

      {/* PARTNERS */}
      <PartnersStrip />

      {/* FINAL CTA — signature floating card */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24" data-testid="final-cta-section">
        <div className="relative overflow-hidden rounded-[2.5rem] teal-depth-scrim text-white p-10 lg:p-16 shadow-[0_40px_80px_-30px_rgba(14,139,133,0.5)]">
          <div className="hero-blob bg-accent/50 w-[380px] h-[380px] -top-20 -right-24" />
          <div className="hero-blob bg-primary/40 w-[320px] h-[320px] -bottom-24 -left-16" />
          <div className="relative z-10 grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <p className="text-xs uppercase tracking-[0.25em] text-amber-300 font-bold mb-3">Créez votre compte</p>
              <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Créez votre compte <span className="italic font-light text-amber-200">dès aujourd'hui</span> !
              </h2>
              <p className="mt-5 text-white/85 max-w-xl text-base sm:text-lg leading-relaxed">
                Accédez simultanément à des professionnels fiables et à des opportunités d'affaires, <strong className="text-amber-200">partout au Canada</strong>.
              </p>
            </div>
            <div className="lg:col-span-2 flex flex-col sm:flex-row gap-3 lg:justify-end">
              <Link to="/inscription" className="btn-orange px-7 py-4 shadow-[0_12px_30px_rgba(249,115,22,0.45)] active:scale-[0.98]" data-testid="final-cta-register-button">
                Créer un compte <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-white/40 rounded-full font-medium hover:bg-white/10 transition backdrop-blur-sm" data-testid="final-cta-partner-button">
                Devenir partenaire
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------- Home sub-sections ----------------------

function ArchitectureVolets({ catsV1, catsV2 }) {
  const [activeVolet, setActiveVolet] = useState("v1");
  const active = activeVolet === "v1" ? catsV1 : catsV2;
  return (
    <div>
      <div className="inline-flex items-center gap-1.5 p-1.5 rounded-full bg-white border border-primary/15 shadow-sm mb-8" role="tablist" data-testid="architecture-tabs">
        <button
          role="tab"
          aria-selected={activeVolet === "v1"}
          onClick={() => setActiveVolet("v1")}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all min-h-[44px] ${
            activeVolet === "v1" ? "bg-primary text-white shadow-md" : "text-foreground/70 hover:bg-primary/5"
          }`}
          data-testid="architecture-tab-etablissement"
        >
          <span className={`font-display font-black text-xs ${activeVolet === "v1" ? "text-amber-300" : "text-primary"}`}>01</span>
          Établissement
        </button>
        <button
          role="tab"
          aria-selected={activeVolet === "v2"}
          onClick={() => setActiveVolet("v2")}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all min-h-[44px] ${
            activeVolet === "v2" ? "bg-accent text-white shadow-md" : "text-foreground/70 hover:bg-accent/5"
          }`}
          data-testid="architecture-tab-entrepreneuriat"
        >
          <span className={`font-display font-black text-xs ${activeVolet === "v2" ? "text-white" : "text-accent"}`}>02</span>
          Entrepreneuriat
        </button>
      </div>
      <div className="mb-5">
        <div className="text-xs uppercase tracking-[0.25em] font-bold" style={{ color: activeVolet === "v1" ? "#0E8B85" : "#F18A2E" }}>
          {activeVolet === "v1" ? "Volet 1 — Établissement" : "Volet 2 — Entrepreneuriat"}
        </div>
        <div className="font-display font-semibold text-base sm:text-lg mt-1 text-foreground">
          {activeVolet === "v1"
            ? "Services essentiels à l'installation et à la vie quotidienne"
            : "Services orientés affaires, développement et investissement"}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 reveal-up">
        {active.map((c) => <CategoryTile key={c.slug} category={c} />)}
      </div>
    </div>
  );
}

function IciQuebecStrip() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    // Now shows ALL Canadian providers (approved & published). Quebec-based ones
    // keep their "En vedette" / "Constitué au Québec" badges via ProfileCard.
    api.get("/profiles", { params: { limit: 12 } })
      .then((r) => setItems(r.data))
      .catch(() => {});
  }, []);
  if (items.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="providers-canada-section">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-primary font-semibold mb-2">
            Prestataires canadiens
          </p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-foreground tracking-tight">Tous les prestataires francophones, en un seul endroit</h2>
          <p className="text-muted-foreground mt-2 max-w-2xl text-sm">Tous les professionnels, entreprises et organismes de toutes les provinces et territoires, référencés et validés par notre équipe.</p>
        </div>
        <Link to="/recherche" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid="providers-view-all">
          Tout voir <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <HScroll testId="ici-quebec-carousel" itemWidthClass="w-72 sm:w-80">
        {items.map((p) => (
          <ProfileCard key={p.id} profile={p} />
        ))}
      </HScroll>
    </section>
  );
}

function LatestBlogPosts() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/blog").then((r) => {
      const published = (r.data || [])
        .filter((p) => p.published && !p.is_demo)
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
        .slice(0, 3);
      setPosts(published);
    }).catch(() => {});
  }, []);
  if (posts.length === 0) return null;
  const categoryLabel = (c) => {
    const map = { immigration: "Immigration", entrepreneuriat: "Entrepreneuriat", evenements: "Événements", temoignages: "Témoignages" };
    return map[c] || (c ? c.charAt(0).toUpperCase() + c.slice(1) : "Article");
  };
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="latest-blog-section">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-primary font-bold mb-3">Médiathèque</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-foreground tracking-tight leading-[1.05]">
            Nouveaux <span className="italic font-light text-primary">articles</span>
          </h2>
          <p className="text-muted-foreground mt-3 max-w-2xl text-base leading-relaxed">Conseils, guides pratiques et récits pour vous aider à vous établir et entreprendre au Canada.</p>
        </div>
        <Link to="/blog" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all" data-testid="latest-blog-all-link">
          Tout le blog <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid lg:grid-cols-5 gap-5 lg:gap-6">
        {posts.map((p, i) => (
          <Link
            key={p.id}
            to={`/blog/${p.slug || p.id}`}
            data-testid={`latest-blog-card-${i}`}
            className={`card-surface card-lift overflow-hidden group flex flex-col reveal-up ${i === 0 ? "lg:col-span-3" : "lg:col-span-2"}`}
            style={{ animationDelay: `${i * 100}ms` }}
          >
            {p.cover_url && (
              <div className={`bg-secondary overflow-hidden flex items-center justify-center ${i === 0 ? "aspect-[16/9]" : "aspect-[2/1]"}`}>
                <img src={p.cover_url} alt={p.title} className="w-full h-full object-contain group-hover:scale-[1.04] transition-transform duration-700 ease-out" loading="lazy" />
              </div>
            )}
            <div className="p-6 lg:p-7 flex flex-col flex-1">
              <p className="text-[11px] uppercase tracking-[0.2em] text-accent font-bold">{categoryLabel(p.category)}</p>
              <h3 className={`font-display font-bold leading-tight mt-2.5 group-hover:text-primary transition-colors ${i === 0 ? "text-xl lg:text-2xl" : "text-lg"}`}>{p.title}</h3>
              {p.excerpt && <p className="text-sm text-muted-foreground mt-3 leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>}
              <div className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold mt-5 group-hover:gap-2.5 transition-all">
                Lire l'article <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
        ))}
      </div>
      <div className="sm:hidden text-center mt-6">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
          Tout le blog <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

function EventsSpotlight() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/events", { params: { status: "upcoming" } })
      .then((r) => setEvents(r.data.slice(0, 3)))
      .catch(() => {});
  }, []);
  if (events.length === 0) return null;
  return (
    <section className="bg-secondary/40 py-16" data-testid="events-spotlight-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-accent font-semibold mb-2">À l'affiche</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-foreground tracking-tight">Événements à venir</h2>
            <p className="text-muted-foreground mt-2 max-w-2xl text-sm">Rendez-vous communautaires, galas et lancements — soutenez ou participez à ceux qui vous parlent.</p>
          </div>
          <Link to="/agenda" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
            Tout l'agenda <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {events.map((e, i) => (
            <Link
              key={e.id}
              to={`/agenda/${e.slug}`}
              data-testid={`spotlight-event-${i}`}
              className="card-surface overflow-hidden group hover:shadow-lg transition-all"
            >
              <div className="aspect-[3/4] bg-secondary overflow-hidden flex items-center justify-center">
                {e.poster_url && <img src={e.poster_url} alt={e.title} className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />}
              </div>
              <div className="p-4">
                <h3 className="font-display font-semibold text-sm leading-snug line-clamp-2 group-hover:text-primary transition-colors">{e.title}</h3>
                <div className="text-xs text-muted-foreground mt-2">{e.date_label} · {e.city}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function AdBannerSection() {
  const [leaderboard, setLeaderboard] = useState(null);
  const [video, setVideo] = useState(null);
  useEffect(() => {
    api.get("/ads/home_leaderboard").then((r) => setLeaderboard(r.data && r.data.placement ? r.data : null)).catch(() => {});
    api.get("/ads/home_video").then((r) => setVideo(r.data && r.data.placement ? r.data : null)).catch(() => {});
  }, []);
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="ad-banner-section">
      <div className="grid md:grid-cols-[2fr_1fr] gap-5">
        {/* Leaderboard 970×250 */}
        {leaderboard && leaderboard.image_url ? (
          <a
            href={leaderboard.target_url || "#"}
            target={leaderboard.target_url ? "_blank" : undefined}
            rel="noreferrer"
            className="relative rounded-3xl overflow-hidden bg-secondary aspect-[970/250] block group"
            data-testid="ad-leaderboard-slot"
          >
            <img src={leaderboard.image_url} alt={leaderboard.title || "Publicité"} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
          </a>
        ) : (
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-primary/95 to-primary text-white min-h-[220px] flex items-center justify-center p-8 border border-primary/20" data-testid="ad-leaderboard-slot">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-widest text-white/70 mb-2">Espace publicité — Leaderboard 970×250</p>
              <h3 className="font-display font-bold text-2xl">Votre marque, ici, devant nos visiteurs francophones.</h3>
              <Link to="/espace-publicite" className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-sm font-medium transition">
                Réserver cet espace <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
        {/* Video 16:9 */}
        {video && video.video_url ? (
          <div className="relative rounded-3xl overflow-hidden bg-black aspect-video" data-testid="ad-video-slot">
            <iframe
              src={video.video_url}
              title={video.title || "Présentation DemkaBiz"}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <div className="relative rounded-3xl overflow-hidden bg-secondary border-2 border-dashed border-accent/30 min-h-[220px] flex flex-col items-center justify-center p-6 text-center" data-testid="ad-video-slot">
            <p className="text-[10px] uppercase tracking-widest text-accent font-semibold mb-2">Emplacement vidéo</p>
            <h3 className="font-display font-semibold text-base leading-snug">Présentation vidéo DemkaBiz</h3>
            <p className="text-xs text-muted-foreground mt-1">En cours de réalisation…</p>
          </div>
        )}
      </div>
    </section>
  );
}

function FleurDeLysHomeSVG() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-4 h-4">
      <path d="M12 2c-.9 0-1.7.9-1.7 2 0 .8.3 1.5.7 1.9-1.4.9-2 2.4-2 4.1 0 1.2.4 2.3 1 3.2H8v2h2v1H8v2h2v3.8l-1.5.5-.5 1 4 .5 4-.5-.5-1-1.5-.5V18h2v-2h-2v-1h2v-2h-2c.6-.9 1-2 1-3.2 0-1.7-.6-3.2-2-4.1.4-.4.7-1.1.7-1.9 0-1.1-.8-2-1.7-2zm-4 8c-1 0-2 .3-2.8.9C4.4 11.5 4 12.4 4 13.3c0 1.3.9 2.4 2 2.7 0-2.4 1-4.5 2-6zm8 0c1 1.5 2 3.6 2 6 1.1-.3 2-1.4 2-2.7 0-.9-.4-1.8-1.2-2.4-.8-.6-1.8-.9-2.8-.9z"/>
    </svg>
  );
}
